import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FormValidationService } from '../../../../services/core/form-validation.service';
import { NewUser } from '../../../../models/user-management/new-user.model';
import { EMPTY, Observable, take, tap } from 'rxjs';
import { valueSelectedValidator } from '../../../../directives/validators/value-selected.directive';
import { TranslateService } from '@ngx-translate/core';
import { UserContact } from '../../../../models/user-management/user-contact.model';
import { UserPayment } from '../../../../models/user-management/user-payment.model';
import { User } from '../../../../store/authentication/model/user.model';
import { UserManagementFacadeService } from '../../../../services/management/user-management-facade.service';
import { catchError } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'app-create-user-modal',
    templateUrl: './create-user-modal.component.html',
    styleUrls: ['./create-user-modal.component.scss']
})
export class CreateUserModalComponent implements OnInit {
    constructor(
        private formBuilder: FormBuilder,
        private ref: DynamicDialogRef,
        private formValidationService: FormValidationService,
        private translate: TranslateService,
        private dynamicDialogConfig: DynamicDialogConfig,
        private userManagementFacadeService: UserManagementFacadeService
    ) {}

    userForm: FormGroup = new FormGroup({});

    data: [UserContact, UserPayment, User] | null = this.dynamicDialogConfig.data;

    ngOnInit() {
        if (this.data !== null) {
            this.userForm = this.formBuilder.group({
                userData: this.formBuilder.group({
                    isGuestAccount: new FormControl<boolean>(false),
                    gender: new FormControl(this.data[2].gender, [Validators.required, valueSelectedValidator()]),
                    userName: new FormControl<string>(this.data[2].userName!, Validators.required),
                    firstName: new FormControl<string>(this.data[2].firstName!, Validators.required),
                    lastName: new FormControl<string>(this.data[2].lastName!, Validators.required),
                    email: new FormControl<string>(this.data[2].email!, [Validators.required, Validators.email]),
                    changePassword: new FormControl<boolean>(false),
                    password: new FormControl<string>({ value: '', disabled: true }),
                    roles: new FormControl<string[]>(this.data[2].roles!),
                    notifyUser: new FormControl<boolean>(false)
                }),
                addressData: this.formBuilder.group({
                    street: new FormControl<string>(this.data[0].street),
                    postalCode: new FormControl<string>(this.data[0].zip),
                    city: new FormControl<string>(this.data[0].city),
                    country: new FormControl<string>(this.data[0].country),
                    phone: new FormControl<string>(this.data[0].phone),
                    mobile: new FormControl<string>(this.data[0].mobile)
                }),
                paymentData: this.formBuilder.group({
                    accountOwner: new FormControl<string>(this.data[1].accountHolder),
                    iban: new FormControl<string>(this.data[1].iban),
                    bic: new FormControl<string>(this.data[1].bic),
                    bankTitle: new FormControl<string>(this.data[1].bank),
                    sepaMandate: new FormControl<string>({ value: this.data[1].sepaMandate, disabled: true })
                }),
                commentData: this.formBuilder.group({
                    comment: new FormControl<string>(this.data[2].comment!),
                    accountingComment: new FormControl<string>(this.data[2].accountingComment!)
                })
            });
        } else {
            this.userForm = this.formBuilder.group({
                userData: this.formBuilder.group({
                    isGuestAccount: new FormControl<boolean>(false),
                    gender: new FormControl('', [Validators.required, valueSelectedValidator()]),
                    userName: new FormControl<string>('', Validators.required),
                    firstName: new FormControl<string>('', Validators.required),
                    lastName: new FormControl<string>('', Validators.required),
                    email: new FormControl<string>('', [Validators.required, Validators.email]),
                    changePassword: new FormControl<boolean>(true),
                    password: new FormControl<string>('', Validators.required),
                    roles: new FormControl<string[]>([]),
                    notifyUser: new FormControl<boolean>(false)
                }),
                addressData: this.formBuilder.group({
                    street: new FormControl<string>(''),
                    postalCode: new FormControl<string>(''),
                    city: new FormControl<string>(''),
                    country: new FormControl<string>(''),
                    phone: new FormControl<string>(''),
                    mobile: new FormControl<string>('')
                }),
                paymentData: this.formBuilder.group({
                    accountOwner: new FormControl<string>(''),
                    iban: new FormControl<string>(''),
                    bic: new FormControl<string>(''),
                    bankTitle: new FormControl<string>(''),
                    sepaMandate: new FormControl<string>({ value: '', disabled: true })
                }),
                commentData: this.formBuilder.group({
                    comment: new FormControl<string>(''),
                    accountingComment: new FormControl<string>('')
                })
            });
        }
    }

    // getter
    get userName() {
        return this.userForm.get('userData.userName');
    }
    get firstName() {
        return this.userForm.get('userData.firstName');
    }
    get lastName() {
        return this.userForm.get('userData.lastName');
    }
    get gender() {
        return this.userForm.get('userData.gender');
    }
    get comment() {
        return this.userForm.get('commentData.comment');
    }
    get accountingComment() {
        return this.userForm.get('commentData.accountingComment');
    }
    get email() {
        return this.userForm.get('userData.email');
    }
    get password() {
        return this.userForm.get('userData.password');
    }
    get roles() {
        return this.userForm.get('userData.roles');
    }
    get notifyUser() {
        return this.userForm.get('userData.notifyUser');
    }
    get isGuestAccount() {
        return this.userForm.get('userData.isGuestAccount');
    }
    get changePassword() {
        return this.userForm.get('userData.changePassword');
    }

    close() {
        this.ref.close();
    }

    save() {
        if (this.userForm.valid) {
            const newUser: NewUser = {
                userName: this.userName?.value,
                firstName: this.firstName?.value,
                lastName: this.lastName?.value,
                gender: this.gender?.value,
                comment: this.comment?.value,
                accountingComment: this.accountingComment?.value,
                email: this.email?.value,
                password: this.password?.value,
                roles: this.roles?.value,
                notifyUser: this.notifyUser?.value,
                changePassword: true,
                trainingRestrictions: []
            };
            if (this.data !== null) {
                // user is edited
                newUser.id = this.data[2].id;
                this.userManagementFacadeService
                    .updateUser(newUser, this.changePassword?.value)
                    .pipe(
                        tap(() => {
                            this.ref.close({
                                severity: 'success',
                                summary: '',
                                detail: this.translate.instant('CORE_SAVE_SUCCESSFULLY')
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return this.setUserFormError(error);
                        }),
                        take(1)
                    )
                    .subscribe();
            } else {
                // new user is created
                this.userManagementFacadeService
                    .createUser(newUser, this.isGuestAccount?.value)
                    .pipe(
                        tap(() => {
                            this.ref.close({
                                severity: 'success',
                                summary: '',
                                detail: this.translate.instant('CORE_SAVE_SUCCESSFULLY')
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return this.setUserFormError(error);
                        }),
                        take(1)
                    )
                    .subscribe();
            }
            this.userForm.markAllAsTouched();
            this.formValidationService.moveToInvalidTab();
        }
    }

    private setUserFormError(error: HttpErrorResponse): Observable<never> {
        if (error.error.errorCode === 'USER_USERNAME_ALREADY_EXISTS') {
            this.userName?.setErrors({ userNameExists: error.error.message });
        } else if (error.error.errorCode === 'USER_EMAIL_ALREADY_EXISTS') {
            this.email?.setErrors({ emailExists: error.error.message });
        }

        return EMPTY;
    }
}
